import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/SEO';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const faqsEn = [
  { q: 'Is this real?', a: 'Yes. Real money. Real leaderboard. Really pointless. Every donation is processed through Stripe and shown live on the public leaderboard.' },
  { q: 'What do I get for my money?', a: 'Absolutely nothing — except your name on the dumbest highscore list on the internet. No products. No perks. Just glory (and shame).' },
  { q: 'Where does the money go?', a: 'It funds the continued operation of The Experiment and the artists behind it. There are no hidden beneficiaries.' },
  { q: 'Can I get a refund?', a: 'You paid for nothing and received exactly that. No refunds.' },
  { q: 'Is DUMONGO legal?', a: 'Yes. Voluntary donations to a transparent project are entirely legal. All payments are PCI-compliant via Stripe.' },
  { q: 'Is this a scam?', a: 'No. A scam implies deception. DUMONGO is fully transparent: zero value promised, zero value delivered. That is the point.' },
  { q: 'Can I be anonymous?', a: 'You choose your own display name. It can be a pseudonym, joke or anything (within the 20-character limit).' },
  { q: 'Why would anyone do this?', a: 'Curiosity. Humor. Status. Art. Boredom. Spite. The reasons are as varied as the Mongos themselves.' },
];

const faqsDe = [
  { q: 'Ist das echt?', a: 'Ja. Echtes Geld. Echtes Leaderboard. Wirklich sinnlos. Jede Spende läuft über Stripe und erscheint live auf der öffentlichen Bestenliste.' },
  { q: 'Was bekomme ich für mein Geld?', a: 'Absolut nichts – außer deinem Namen auf der dümmsten Highscore-Liste des Internets. Keine Produkte. Keine Vorteile. Nur Ruhm (und Schande).' },
  { q: 'Wohin geht das Geld?', a: 'Es finanziert den weiteren Betrieb des Experiments und die dahinterstehenden Künstler. Keine versteckten Empfänger.' },
  { q: 'Kann ich mein Geld zurückbekommen?', a: 'Du hast für nichts bezahlt und genau das erhalten. Keine Rückerstattung.' },
  { q: 'Ist DUMONGO legal?', a: 'Ja. Freiwillige Spenden an ein transparentes Projekt sind völlig legal. Alle Zahlungen sind PCI-konform über Stripe.' },
  { q: 'Ist das Betrug?', a: 'Nein. Betrug setzt Täuschung voraus. DUMONGO ist vollständig transparent: null Wert versprochen, null Wert geliefert. Das ist der Punkt.' },
  { q: 'Kann ich anonym bleiben?', a: 'Du wählst deinen eigenen Anzeigenamen. Pseudonym, Witzname oder beliebig (max. 20 Zeichen).' },
  { q: 'Warum macht jemand sowas?', a: 'Neugier. Humor. Status. Kunst. Langeweile. Trotz. Die Gründe sind so vielfältig wie die Mongos selbst.' },
];

const FAQ = () => {
  const { i18n } = useTranslation();
  const isDe = i18n.language?.startsWith('de');
  const faqs = isDe ? faqsDe : faqsEn;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background px-4 py-16">
      <SEO
        title={isDe ? 'FAQ – Häufige Fragen' : 'FAQ – Frequently Asked Questions'}
        description={
          isDe
            ? 'Antworten zu DUMONGO.COM: Ist das echt? Wohin geht das Geld? Bekomme ich eine Rückerstattung? Alles, was du wissen musst, bevor du Geld verbrennst.'
            : 'Answers about DUMONGO.COM: Is it real? Where does the money go? Can I get a refund? Everything you need to know before burning money.'
        }
        path="/faq"
        jsonLd={jsonLd}
      />
      <header className="fixed top-0 right-0 z-50 p-4">
        <LanguageSwitcher />
      </header>

      <article className="mx-auto max-w-3xl space-y-8">
        <h1 className="text-4xl md:text-6xl font-bold terminal-glow">
          [ FAQ ]
        </h1>
        <p className="text-muted-foreground">
          {isDe ? 'Bevor du fragst – hier sind die Antworten.' : 'Before you ask — here are the answers.'}
        </p>

        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details key={i} className="terminal-border p-6 group">
              <summary className="cursor-pointer font-bold text-primary text-lg list-none flex justify-between items-center">
                <span>&gt; {f.q}</span>
                <span className="group-open:rotate-90 transition-transform">▶</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        <nav className="flex gap-4 flex-wrap">
          <Link to="/" className="terminal-border px-6 py-3 hover:bg-primary hover:text-primary-foreground transition-colors">
            {isDe ? '← ZURÜCK' : '← BACK'}
          </Link>
          <Link to="/about" className="terminal-border px-6 py-3 hover:bg-primary hover:text-primary-foreground transition-colors">
            {isDe ? 'ÜBER UNS' : 'ABOUT'} →
          </Link>
        </nav>
      </article>
    </div>
  );
};

export default FAQ;
